import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, authenticateAnonymously } from '../firebase';
import { Standing, Team } from '../types';

interface StandingsViewProps {
  team: Team;
}

export const StandingsView: React.FC<StandingsViewProps> = ({ team }) => {
  const [standings, setStandings] = useState<Standing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setupRealtimeListener = async () => {
      try {
        setLoading(true);

        // Erst authentifizieren
        const authSuccess = await authenticateAnonymously();
        if (!authSuccess) {
          setError('Authentifizierung fehlgeschlagen');
          setLoading(false);
          return;
        }

        const standingsRef = doc(db, 'standings', team.standingCollection);

        // Real-time listener
        unsubscribe = onSnapshot(standingsRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            const standingsData = data.standings || [];
            console.log('Firestore standings data:', standingsData);

            // Map Firestore data to our interface using correct field names
            const mappedStandings = standingsData.map((standing: any) => ({
              id: standing.id || standing.team,
              team: standing.team,
              games: parseInt(standing.matches) || 0,  // Use "matches" field
              wins: parseInt(standing.wins || standing.w) || 0,
              draws: parseInt(standing.draws || standing.d) || 0,
              losses: parseInt(standing.losses || standing.l) || 0,
              goalsFor: standing.goals ? parseInt(standing.goals.split(':')[0]) || 0 : 0,  // Extract from "goals" field
              goalsAgainst: standing.goals ? parseInt(standing.goals.split(':')[1]) || 0 : 0,  // Extract from "goals" field
              goalDifference: parseInt(standing.diff) || 0,  // Use "diff" field directly
              points: standing.points || standing.pts || "",
              position: parseInt(standing.position) || 0,
              totalGames: standing.totalGames || null
            }));

            if (mappedStandings.length > 0) {
              console.log('Mapped standing object:', mappedStandings[0]);
              console.log('Original keys:', Object.keys(standingsData[0]));
            }

            // Sort by position
            mappedStandings.sort((a: Standing, b: Standing) => a.position - b.position);
            setStandings(mappedStandings);
            setError(null);
          } else {
            setError('Tabelle nicht gefunden');
          }
          setLoading(false);
        }, (err) => {
          setError('Fehler beim Laden der Tabelle');
          console.error('Error with standings listener:', err);
          setLoading(false);
        });

      } catch (err) {
        setError('Fehler beim Setup der Tabelle');
        console.error('Error setting up standings listener:', err);
        setLoading(false);
      }
    };

    setupRealtimeListener();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [team]);

  if (loading) {
    return (
      <div>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fortuna-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Lade Tabelle...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="text-center py-8 text-red-600">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-1">#</th>
              <th className="text-left py-2 px-2">Team</th>
              <th className="text-center py-2 px-1">Sp</th>
              <th className="text-center py-2 px-1">S</th>
              <th className="text-center py-2 px-1">U</th>
              <th className="text-center py-2 px-1">N</th>
              <th className="text-center py-2 px-1 hidden min-[406px]:table-cell">Tore</th>
              <th className="text-center py-2 px-1">Diff</th>
              <th className="text-center py-2 px-1 font-bold">Pkt</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((standing) => (
              <tr
                key={standing.id || standing.team}
                className={`border-b border-gray-100 hover:bg-gray-50 ${
                  standing.team.toLowerCase().includes('fortuna')
                    ? 'bg-fortuna-50 font-medium'
                    : ''
                }`}
              >
                <td className="py-2 px-1 font-medium">{standing.position}</td>
                <td className="py-2 px-2 truncate max-w-32" title={standing.team}>
                  {standing.team}
                </td>
                <td className="py-2 px-1 text-center">
                  {standing.totalGames ? `${standing.games}/${standing.totalGames}` : standing.games}
                </td>
                <td className="py-2 px-1 text-center">{standing.wins}</td>
                <td className="py-2 px-1 text-center">{standing.draws}</td>
                <td className="py-2 px-1 text-center">{standing.losses}</td>
                <td className="py-2 px-1 text-center hidden min-[406px]:table-cell">
                  {standing.goalsFor}:{standing.goalsAgainst}
                </td>
                <td className={`py-2 px-1 text-center ${
                  standing.goalDifference > 0
                    ? 'text-green-600'
                    : standing.goalDifference < 0
                    ? 'text-red-600'
                    : ''
                }`}>
                  {standing.goalDifference > 0 ? '+' : ''}{standing.goalDifference}
                </td>
                <td className="py-2 px-1 text-center font-bold text-fortuna-600">
                  {standing.points}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {standings.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Keine Tabelle gefunden
          </div>
        )}
      </div>
    </div>
  );
};